export type RedactionStyle = "solid" | "mosaic" | "blur";

export type RedactionBox = {
  id: string;
  /** Normalized 0–1 relative to image */
  x: number;
  y: number;
  w: number;
  h: number;
  style: RedactionStyle;
};

export const REDACTION_STYLE_OPTIONS: {
  value: RedactionStyle;
  label: string;
  description: string;
}[] = [
  {
    value: "solid",
    label: "Tömör box",
    description: "Fekete/szürke kitakarás",
  },
  {
    value: "mosaic",
    label: "Mozaik",
    description: "Pixeles / magánadat-szerű",
  },
  {
    value: "blur",
    label: "Elmosás",
    description: "Lágyabb, kevésbé erős",
  },
];

const MAX_EDGE = 2400;
const OUTPUT_QUALITY = 0.92;
const SKIP_TYPES = new Set([
  "image/gif",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon",
]);

export function canRedactImageFile(file: Pick<File, "type" | "name">) {
  const type = file.type || "";
  if (SKIP_TYPES.has(type)) {
    return false;
  }
  if (type.startsWith("image/")) {
    return true;
  }
  const lower = file.name.toLowerCase();
  return (
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".png") ||
    lower.endsWith(".webp")
  );
}

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

function fallbackId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `redact-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function normalizeRedactionBox(
  box: Omit<RedactionBox, "id"> & { id?: string },
  id = box.id ?? fallbackId(),
): RedactionBox {
  const x = clamp01(box.x);
  const y = clamp01(box.y);
  const w = Math.max(0.02, Math.min(1 - x, box.w));
  const h = Math.max(0.02, Math.min(1 - y, box.h));
  return { id, x, y, w, h, style: box.style };
}

async function bitmapFromFile(file: File) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("A kép nem tölthető be."));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function scaledSize(width: number, height: number) {
  const longest = Math.max(width, height);
  if (longest <= MAX_EDGE) {
    return { width, height };
  }
  const ratio = MAX_EDGE / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function outputType() {
  if (typeof document === "undefined") {
    return "image/jpeg" as const;
  }
  const canvas = document.createElement("canvas");
  return canvas.toDataURL("image/webp").startsWith("data:image/webp")
    ? ("image/webp" as const)
    : ("image/jpeg" as const);
}

function extensionFor(type: string) {
  return type === "image/webp" ? "webp" : "jpg";
}

function replaceExtension(name: string, extension: string) {
  return name.replace(/\.[^.]+$/u, "") + `.${extension}`;
}

function applySolid(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  ctx.fillStyle = "#111827";
  ctx.fillRect(x, y, w, h);
}

function applyMosaic(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const tile = Math.max(8, Math.round(Math.min(w, h) / 14));
  const sw = Math.max(1, Math.floor(w / tile));
  const sh = Math.max(1, Math.floor(h / tile));
  const temp = document.createElement("canvas");
  temp.width = sw;
  temp.height = sh;
  const tempCtx = temp.getContext("2d");
  if (!tempCtx) {
    applySolid(ctx, x, y, w, h);
    return;
  }
  tempCtx.imageSmoothingEnabled = false;
  tempCtx.drawImage(source, x, y, w, h, 0, 0, sw, sh);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(temp, 0, 0, sw, sh, x, y, w, h);
  ctx.imageSmoothingEnabled = true;
}

function applyBlur(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  canvasWidth: number,
  canvasHeight: number,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  if ("filter" in ctx) {
    ctx.filter = "blur(18px)";
    ctx.drawImage(source, 0, 0, canvasWidth, canvasHeight);
    ctx.filter = "none";
  } else {
    // Fallback mosaic-ish blur for older engines
    applyMosaic(ctx, source, x, y, w, h);
  }
  ctx.restore();
}

export async function bakeRedactedImageFile(input: {
  file: File;
  boxes: RedactionBox[];
}): Promise<File> {
  if (!canRedactImageFile(input.file)) {
    throw new Error("Ez a fájltípus nem takarható ki.");
  }
  if (input.boxes.length === 0) {
    return input.file;
  }

  const source = await bitmapFromFile(input.file);
  const { width, height } = scaledSize(source.width, source.height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    if ("close" in source && typeof source.close === "function") {
      source.close();
    }
    throw new Error("A kitakarás nem sikerült.");
  }

  ctx.drawImage(source, 0, 0, width, height);

  const base = document.createElement("canvas");
  base.width = width;
  base.height = height;
  const baseCtx = base.getContext("2d");
  if (!baseCtx) {
    if ("close" in source && typeof source.close === "function") {
      source.close();
    }
    throw new Error("A kitakarás nem sikerült.");
  }
  baseCtx.drawImage(canvas, 0, 0);

  for (const box of input.boxes) {
    const x = Math.round(box.x * width);
    const y = Math.round(box.y * height);
    const w = Math.max(2, Math.round(box.w * width));
    const h = Math.max(2, Math.round(box.h * height));

    if (box.style === "solid") {
      applySolid(ctx, x, y, w, h);
    } else if (box.style === "mosaic") {
      applyMosaic(ctx, base, x, y, w, h);
    } else {
      applyBlur(ctx, base, width, height, x, y, w, h);
    }
  }

  if ("close" in source && typeof source.close === "function") {
    source.close();
  }

  const type = outputType();
  const blob = await canvasToBlob(canvas, type, OUTPUT_QUALITY);
  if (!blob) {
    throw new Error("A kitakart kép mentése nem sikerült.");
  }

  return new File([blob], replaceExtension(input.file.name, extensionFor(type)), {
    type,
    lastModified: Date.now(),
  });
}
